import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import axios from "axios";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { appendSasToken, removeSasFromDescription } from "../../Imagehelper";
import { fields } from "../constants";
import { usePageDocumentDetails } from "./List.hooks";
import { FetchLocationAirport, FetchSubModuleCentralized } from "./ListOptions";

export const CentralizedAddEditList = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [shopTypeId, setShopTypeId] = useState<string | null>(null);

  const {
    data: editPageContent,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = usePageDocumentDetails(query.get("editId") || "");

  const {
    data: editPageContentById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    const activeTab = query.get("activeTab");
    if (activeTab === "pageHeading") {
      refetchCategories();
    }
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("activeTab"), query.get("editId"), query.get("id")]);
  useEffect(() => {
    const fetchShopType = async () => {
      if (!query.get("listType")) return;

      try {
        const response = await axios.get(`${centralizedHost}/api/type-shop-dine`);
        if (response.data && Array.isArray(response.data)) {
          const matchedType = response.data.find((type) => type.slug === query.get("listType"));
          if (matchedType) {
            setShopTypeId(String(matchedType.id));
          }
        }
      } catch (error) {
        console.error("Error fetching shop types:", error);
      }
    };

    fetchShopType();
  }, [query.get("listType")]);

  const formConfig: FormConfig = {
    initialValues: {
      // ================= IMAGES =================
      image: editPageContentById
        ? editPageContentById.payload?.image
          ? [
              {
                uid: "-1",
                name: "image.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.image),
              },
            ]
          : []
        : editPageContent?.image
          ? [
              {
                uid: "-1",
                name: "image.jpg",
                status: "done",
                url: appendSasToken(editPageContent.image),
              },
            ]
          : [],

      mobile_image: editPageContentById
        ? editPageContentById.payload?.mobile_image
          ? [
              {
                uid: "-1",
                name: "mobile_image.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.mobile_image),
              },
            ]
          : []
        : editPageContent?.mobile_image
          ? [
              {
                uid: "-1",
                name: "mobile_image.jpg",
                status: "done",
                url: appendSasToken(editPageContent.mobile_image),
              },
            ]
          : [],

      banner_image: editPageContentById
        ? editPageContentById.payload?.banner_image
          ? [
              {
                uid: "-1",
                name: "banner_image.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.banner_image),
              },
            ]
          : []
        : editPageContent?.banner_image
          ? [
              {
                uid: "-1",
                name: "banner_image.jpg",
                status: "done",
                url: appendSasToken(editPageContent.banner_image),
              },
            ]
          : [],

      // ================= TITLES =================
      title: editPageContentById
        ? decodeHTML(editPageContentById.payload?.title)
        : editPageContent
          ? decodeHTML(editPageContent?.title)
          : "",

      title_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.title_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.title_malay)
          : "",

      title_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.title_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.title_mandarin)
          : "",

      // ================= OUTLET DETAILS =================
      // outlet: editPageContentById
      //   ? decodeHTML(editPageContentById.payload?.outlet)
      //   : editPageContent
      //     ? decodeHTML(editPageContent?.outlet)
      //     : "",

      store_time: editPageContentById
        ? decodeHTML(editPageContentById.payload?.store_time)
        : editPageContent
          ? decodeHTML(editPageContent?.store_time)
          : "",

      // ================= MULTI-SELECT ARRAYS =================
      brand: editPageContentById ? (editPageContentById.payload?.brand ?? []) : (editPageContent?.brand ?? []),

      source: editPageContentById ? (editPageContentById.payload?.source ?? []) : (editPageContent?.source ?? []),

      type_poi: editPageContentById ? (editPageContentById.payload?.type_poi ?? []) : (editPageContent?.type_poi ?? []),

      data_classification: editPageContentById
        ? (editPageContentById.payload?.data_classification ?? [])
        : (editPageContent?.data_classification ?? []),

      shop_category: editPageContentById
        ? (editPageContentById.payload?.shop_category ?? null)
        : (editPageContent?.shop_category ?? null),

      sub_category: editPageContentById
        ? (editPageContentById.payload?.sub_category ?? [])
        : (editPageContent?.sub_category ?? []),

      tag: editPageContentById ? (editPageContentById.payload?.tag ?? []) : (editPageContent?.tag ?? []),

      // type: editPageContentById ? (editPageContentById.payload?.type ?? []) : (editPageContent?.type ?? []),

      location_code: editPageContentById
        ? decodeHTML(editPageContentById.payload?.location_code ?? [])
        : editPageContent
          ? decodeHTML(editPageContent?.location_code ?? [])
          : undefined,

      field_project_airport: editPageContentById
        ? decodeHTML(editPageContentById.payload?.field_project_airport)
        : editPageContent
          ? decodeHTML(editPageContent?.field_project_airport)
          : undefined,

      redirect_url_id: editPageContentById
        ? decodeHTML(editPageContentById.payload?.redirect_url_id)
        : editPageContent
          ? decodeHTML(editPageContent?.redirect_url_id)
          : undefined,

      // ================= IMAGE DESCRIPTIONS =================
      image_description: editPageContentById
        ? decodeHTML(editPageContentById.payload?.image_description)
        : editPageContent
          ? decodeHTML(editPageContent?.image_description)
          : "",

      image_description_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.image_description_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.image_description_malay)
          : "",

      image_description_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.image_description_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.image_description_mandarin)
          : "",

      // ================= DATE FIELDS =================
      start_date: editPageContentById ? editPageContentById.payload?.start_date : (editPageContent?.start_date ?? ""),

      end_state: editPageContentById ? editPageContentById.payload?.end_state : (editPageContent?.end_state ?? ""),

      // ================= BOOLEAN FIELDS =================

      is_active: editPageContentById
        ? decodeHTML(editPageContentById.payload?.is_active)
        : editPageContent
          ? decodeHTML(editPageContent?.is_active)
          : "",

      is_temporarily_closed: editPageContentById
        ? decodeHTML(editPageContentById.payload?.is_temporarily_closed)
        : editPageContent
          ? decodeHTML(editPageContent?.is_temporarily_closed)
          : "",

      gender_separated: editPageContentById
        ? decodeHTML(editPageContentById.payload?.gender_separated)
        : editPageContent
          ? decodeHTML(editPageContent?.gender_separated)
          : "",

      braille_signage: editPageContentById
        ? decodeHTML(editPageContentById.payload?.braille_signage)
        : editPageContent
          ? decodeHTML(editPageContent?.braille_signage)
          : "",

      wheel_chair: editPageContentById
        ? decodeHTML(editPageContentById.payload?.wheel_chair)
        : editPageContent
          ? decodeHTML(editPageContent?.wheel_chair)
          : "",

      published: editPageContentById
        ? decodeHTML(editPageContentById.payload?.published)
        : editPageContent
          ? decodeHTML(editPageContent?.published)
          : "",

      // ================= SEO =================
      seo_title: editPageContentById
        ? decodeHTML(editPageContentById.payload?.seo_title)
        : editPageContent
          ? decodeHTML(editPageContent?.seo_title)
          : "",

      seo_description: editPageContentById
        ? decodeHTML(editPageContentById.payload?.seo_description)
        : editPageContent
          ? decodeHTML(editPageContent?.seo_description)
          : "",

      // ================= OTHER FIELDS =================
      closure_reason: editPageContentById
        ? decodeHTML(editPageContentById.payload?.closure_reason)
        : editPageContent
          ? decodeHTML(editPageContent?.closure_reason)
          : "",

      latitude: editPageContentById
        ? (editPageContentById.payload?.latitude ?? null)
        : (editPageContent?.latitude ?? null),

      longitude: editPageContentById
        ? (editPageContentById.payload?.longitude ?? null)
        : (editPageContent?.longitude ?? null),

      phone: editPageContentById ? (editPageContentById.payload?.phone ?? "") : (editPageContent?.phone ?? ""),

      email: editPageContentById
        ? decodeHTML(editPageContentById.payload?.email)
        : editPageContent
          ? decodeHTML(editPageContent?.email)
          : "",

      regulatory_compliance: editPageContentById
        ? decodeHTML(editPageContentById.payload?.regulatory_compliance)
        : editPageContent
          ? decodeHTML(editPageContent?.regulatory_compliance)
          : "",

      technical_id: editPageContentById
        ? (editPageContentById.payload?.technical_id ?? "")
        : (editPageContent?.technical_id ?? ""),

      tenant_id: editPageContentById
        ? (editPageContentById.payload?.tenant_id ?? "")
        : (editPageContent?.tenant_id ?? ""),

      centralize_zone:
        decodeHTML(editPageContentById?.payload?.centralize_zone) ||
        decodeHTML(editPageContent?.centralize_zone) ||
        undefined,
      centralize_zone_level:
        decodeHTML(editPageContentById?.payload?.centralize_zone_level) ||
        decodeHTML(editPageContent?.centralize_zone_level) ||
        undefined,
    },

    fields: fields.centralizedList.map((field: any) => {
      if (field.name === "centralize_zone_level") {
        return {
          ...field,
          // note: "Kindly select the Zone before selecting the Zone Level",
          loadOptions: async (zone: string) => {
            return await FetchSubModuleCentralized(
              zone ||
                decodeHTML(editPageContentById?.payload?.centralize_zone) ||
                decodeHTML(editPageContent?.centralize_zone),
              ""
            );
          },
        };
      }
      if (field.name === "location_code") {
        return {
          ...field,
          // note: "Kindly select the Zone before selecting the Zone Level",
          loadOptions: async (airport: string) => {
            return await FetchLocationAirport(
              airport ||
                decodeHTML(editPageContentById?.payload?.field_project_airport) ||
                decodeHTML(editPageContent?.field_project_airport)
            );
          },
        };
      }
      return field;
    }),
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["image", "mobile_image", "banner_image"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CENTRALIZED");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          const cleanUrl = field[0].url.split("?sp=")[0];
          data[key] = cleanUrl;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    let descriptionKeys: any = ["image_description", "image_description_malay", "image_description_mandarin"];
    for (const key of descriptionKeys) {
      if (data[key]) {
        data[key] = removeSasFromDescription(data[key]);
      }
    }

    const payload = {
      action,
      method,
      module: "Centralized Shop & Dine",
      subModule: `${query.get("listType")}_centralizedList`,
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes,
      appList: [{ appName: "CENTRALIZED", apiUrl }],
      payload: {
        ...(action === "UPDATE" && {
          id: editPageContent?.id || editPageContentById?.payload?.id,
        }),
        ...data,
        type: shopTypeId,
      },
      status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} List`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${centralizedHost}/api/shop-store/${editPageContent?.id || editPageContentById?.payload?.id}` : `${centralizedHost}/api/shop-store`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Shop & Dine"
        enableDynamicWatch={true}
      />
    </ContentWrapper>
  );
};
